/**
 * Centralized API Configuration
 * All API endpoints and base URLs are configured here
 *
 * API Strategy:
 * - Development: Uses "proxy" field in package.json to route /api requests to backend
 * - Production: Nginx acts as reverse proxy, routing /api requests to backend
 *
 * This approach eliminates CORS issues and allows for Same-Origin requests
 */

// API_BASE_URL is intentionally empty for Same-Origin requests
// Can be extended in future if needed for different deployment scenarios
const API_BASE_URL = '';

export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: `${API_BASE_URL}/api/auth/login`,
    LOGOUT: `${API_BASE_URL}/api/auth/logout`,
    ME: `${API_BASE_URL}/api/auth/me`,
    PROFILE: `${API_BASE_URL}/api/auth/profile`,
    PROFILE_PICTURE_UPLOAD: `${API_BASE_URL}/api/auth/profile-picture`,
    PROFILE_PICTURE_DELETE: `${API_BASE_URL}/api/auth/profile-picture`,
    FORGOT_PASSWORD: `${API_BASE_URL}/api/auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password`,
    VALIDATE_RESET_TOKEN: `${API_BASE_URL}/api/auth/validate-reset-token`,
    CHANGE_PASSWORD: `${API_BASE_URL}/api/auth/change-password`,
    PROFILE_PICTURE: (userId) => `${API_BASE_URL}/api/auth/profile-picture/${userId}`,
    PREFERENCES: `${API_BASE_URL}/api/auth/preferences`,
    SESSION_CHECK: `${API_BASE_URL}/api/auth/session/check`,
    SESSION_CLOSE: `${API_BASE_URL}/api/auth/session/close`,
    ADMIN_SECURITY_METRICS: `${API_BASE_URL}/api/auth/admin/security-metrics`,
  },

  // Devices endpoints
  DEVICES: {
    LIST: `${API_BASE_URL}/api/devices`,
    GET: (id) => `${API_BASE_URL}/api/devices/${id}`,
    GET_BY_ID: (id) => `${API_BASE_URL}/api/devices/${id}`,
    CREATE: `${API_BASE_URL}/api/devices`,
    UPDATE: (id) => `${API_BASE_URL}/api/devices/${id}`,
    DELETE: (id) => `${API_BASE_URL}/api/devices/${id}`,
    FILTER: `${API_BASE_URL}/api/devices/filter`,
    FILTER_IDS: `${API_BASE_URL}/api/devices/filter/ids`,
    QR_TOKEN: (deviceIdentifier) => `${API_BASE_URL}/api/devices/${encodeURIComponent(deviceIdentifier)}/qr-token`,
    QR_DEVICE: (token) => `${API_BASE_URL}/api/devices/qr/${encodeURIComponent(token)}`,
    BULK_QR_TOKENS: `${API_BASE_URL}/api/devices/bulk-qr-tokens`,
    LOOKUP_BY_CODE: (code) => `${API_BASE_URL}/api/devices/list/lookup/id/${encodeURIComponent(code)}`,
    TECHNICIANS: (id) => `${API_BASE_URL}/api/devices/${id}/technicians`,
    DOCUMENTS: (id) => `${API_BASE_URL}/api/devices/${id}/documents`,
    SUBMIT_FOR_APPROVAL: (id) => `${API_BASE_URL}/api/devices/${id}/submit-for-approval`,
    ASSIGNED_TO_ME: `${API_BASE_URL}/api/devices/assigned-to-me`,
    GET_ASSIGNED_TO_ME: `${API_BASE_URL}/api/devices/assigned-to-me`,
    RELIABILITY_METRICS: (id) => `${API_BASE_URL}/api/devices/${id}/reliability-metrics`,
    MAINTENANCE: (id) => `${API_BASE_URL}/api/devices/${id}/maintenance`,
    AUDIT_HISTORY: (id, category) => {
      const query = category ? `?category=${encodeURIComponent(category)}` : '';
      return `${API_BASE_URL}/api/devices/${id}/audit-history${query}`;
    },
    COMPLAINT_HISTORY: (id) => `${API_BASE_URL}/api/devices/${id}/complaint-history`,
  },

  // Complaints endpoints
  COMPLAINTS: {
    LIST: `${API_BASE_URL}/api/complaints`,
    CREATE: `${API_BASE_URL}/api/complaints`,
    FILTER: `${API_BASE_URL}/api/complaints/filter`,
    GET_ALL: `${API_BASE_URL}/api/complaints`,
    GET: (complaintId) => `${API_BASE_URL}/api/complaints/${complaintId}`,
    GET_STATUS_HISTORY: (complaintId) => `${API_BASE_URL}/api/complaints/${complaintId}/status-history`,
    DOCUMENTS: (complaintId) => `${API_BASE_URL}/api/complaints/${complaintId}/documents`,
    DOCUMENT: (complaintId, docId) => `${API_BASE_URL}/api/complaints/${complaintId}/documents/${docId}`,
    DEVICE_COMPLAINT_DOCUMENTS: (deviceId, excludeComplaintId, countOnly = false) => {
      const params = new URLSearchParams({
        exclude_complaint_id: String(excludeComplaintId),
      });
      if (countOnly) params.set('count_only', 'true');
      return `${API_BASE_URL}/api/devices/${deviceId}/complaint-documents?${params.toString()}`;
    },
  },

  // Location transfers (cross-location)
  LOCATION_TRANSFERS: {
    LIST: `${API_BASE_URL}/api/device-transfers`,
    OPEN_BY_DEVICE: (deviceId) =>
      `${API_BASE_URL}/api/device-transfers/open?device_id=${deviceId}`,
    PRIOR_REJECTIONS: (deviceId) =>
      `${API_BASE_URL}/api/device-transfers/prior-rejections?device_id=${deviceId}`,
    DESTINATION_LOCATIONS: (deviceId) =>
      `${API_BASE_URL}/api/device-transfers/destination-locations?device_id=${deviceId}`,
    UPDATE: (id) => `${API_BASE_URL}/api/device-transfers/${id}`,
    DASHBOARD_COUNTS: `${API_BASE_URL}/api/device-transfers/dashboard-counts`,
    ACTION_SUMMARY: `${API_BASE_URL}/api/device-transfers/action-summary`,
    GET: (id) => `${API_BASE_URL}/api/device-transfers/${id}`,
    DEVICE_PREVIEW: (id) => `${API_BASE_URL}/api/device-transfers/${id}/device-preview`,
    CREATE: `${API_BASE_URL}/api/device-transfers`,
    SUBMIT: (id) => `${API_BASE_URL}/api/device-transfers/${id}/submit`,
    CANCEL: (id) => `${API_BASE_URL}/api/device-transfers/${id}/cancel`,
    APPROVE_SOURCE: (id) => `${API_BASE_URL}/api/device-transfers/${id}/approve-source`,
    REJECT_SOURCE: (id) => `${API_BASE_URL}/api/device-transfers/${id}/reject-source`,
    ACCEPT_DESTINATION: (id) => `${API_BASE_URL}/api/device-transfers/${id}/accept-destination`,
    REJECT_DESTINATION: (id) => `${API_BASE_URL}/api/device-transfers/${id}/reject-destination`,
  },

  // Department transfers (same-location custodian handoff)
  DEPARTMENT_TRANSFERS: {
    LIST: `${API_BASE_URL}/api/department-transfers`,
    ACTION_SUMMARY: `${API_BASE_URL}/api/department-transfers/action-summary`,
    OPEN_BY_DEVICE: (deviceId) =>
      `${API_BASE_URL}/api/department-transfers/open?device_id=${deviceId}`,
    DESTINATION_DEPARTMENTS: (deviceId) =>
      `${API_BASE_URL}/api/department-transfers/destination-departments?device_id=${deviceId}`,
    CREATE: `${API_BASE_URL}/api/department-transfers`,
    CANCEL: (id) => `${API_BASE_URL}/api/department-transfers/${id}/cancel`,
    ACCEPT_DESTINATION: (id) =>
      `${API_BASE_URL}/api/department-transfers/${id}/accept-destination`,
    REJECT_DESTINATION: (id) =>
      `${API_BASE_URL}/api/department-transfers/${id}/reject-destination`,
  },

  // Approvals endpoints
  APPROVALS: {
    PENDING: `${API_BASE_URL}/api/approvals/pending`,
    COUNTS: `${API_BASE_URL}/api/approvals/counts`,
    MY_PENDING: `${API_BASE_URL}/api/approvals/my-pending`,
    GET: (id) => `${API_BASE_URL}/api/approvals/${id}`,
    APPROVE: (id) => `${API_BASE_URL}/api/approvals/${id}/approve`,
    REJECT: (id) => `${API_BASE_URL}/api/approvals/${id}/reject`,
    BY_DEVICE: (deviceId) => `${API_BASE_URL}/api/approvals/pending?device_id=${deviceId}`,
  },

  // Organizations endpoints
  ORGANIZATIONS: {
    GET: (id) => `${API_BASE_URL}/api/organizations/${id}`,
    PUBLIC_BRANDING: `${API_BASE_URL}/api/public/branding`,
  },

  // Departments endpoints
  DEPARTMENTS: {
    LIST: `${API_BASE_URL}/api/departments`,
    CREATE: `${API_BASE_URL}/api/departments`,
    UPDATE: (id) => `${API_BASE_URL}/api/departments/${id}`,
    DELETE: (id) => `${API_BASE_URL}/api/departments/${id}`,
  },

  // Locations endpoints
  LOCATIONS: {
    LIST: `${API_BASE_URL}/api/locations`,
    OVERVIEW: `${API_BASE_URL}/api/locations/overview`,
    GET: (id) => `${API_BASE_URL}/api/locations/${id}`,
    CREATE: `${API_BASE_URL}/api/locations`,
    UPDATE: (id) => `${API_BASE_URL}/api/locations/${id}`,
    DELETE: (id) => `${API_BASE_URL}/api/locations/${id}`,
  },

  // Location complaint TAT (versioned policy per location)
  COMPLAINT_TAT: {
    SUMMARY: (locationId) => `${API_BASE_URL}/api/locations/${locationId}/complaint-tat/summary`,
    VERSIONS: (locationId) => `${API_BASE_URL}/api/locations/${locationId}/complaint-tat/versions`,
    VERSION: (locationId, versionId) =>
      `${API_BASE_URL}/api/locations/${locationId}/complaint-tat/versions/${versionId}`,
    AUDIT: (locationId) => `${API_BASE_URL}/api/locations/${locationId}/complaint-tat/audit`,
  },

  // Users endpoints
  USERS: {
    LIST: `${API_BASE_URL}/api/users`,
    GET: (id) => `${API_BASE_URL}/api/users/${id}`,
    CREATE: `${API_BASE_URL}/api/users`,
    UPDATE: (id) => `${API_BASE_URL}/api/users/${id}`,
    DELETE: (id) => `${API_BASE_URL}/api/users/${id}`,
    DELETE_WITH_DEACTIVATE: (id) => `${API_BASE_URL}/api/users/${id}?allow_deactivate=true`,
    DELETION_PREVIEW: (id) => `${API_BASE_URL}/api/users/${id}/deletion-preview`,
    LOOKUP_BY_EMPLOYEE_ID: (employeeId) => `${API_BASE_URL}/api/users/lookup/employee/${encodeURIComponent(employeeId)}`,
    REVOKE_PENDING_LINK: (id) => `${API_BASE_URL}/api/users/${id}/revoke-pending-link`,
    BY_ROLE: (role, params) => {
      const query = new URLSearchParams(params).toString();
      return `${API_BASE_URL}/api/users/role/${role}${query ? '?' + query : ''}`;
    },
    TECHNICIANS: (params) => {
      const query = new URLSearchParams(params).toString();
      return `${API_BASE_URL}/api/technicians${query ? '?' + query : ''}`;
    },
  },

  // Developer Settings (superadmin only, demo/test environments)
  DEV_SETTINGS: {
    GET: `${API_BASE_URL}/api/dev-settings`,
    UPDATE: `${API_BASE_URL}/api/dev-settings`,
    RESET: `${API_BASE_URL}/api/dev-settings/reset`,
  },

  // System Controls (superadmin only; prod-allowed when enabled)
  SYSTEM_CONTROLS: {
    GET: `${API_BASE_URL}/api/system-controls`,
    EFFECTIVE: `${API_BASE_URL}/api/system-controls/effective`,
    UPDATE: `${API_BASE_URL}/api/system-controls`,
    NOTIFICATION_PREFERENCES: `${API_BASE_URL}/api/system-controls/notification-preferences`,
    NOTIFICATION_PREFERENCES_RESET: `${API_BASE_URL}/api/system-controls/notification-preferences/reset`,
    BRANDING: `${API_BASE_URL}/api/system-controls/branding`,
  },

  // Email endpoints (consolidated in email.py blueprint)
  EMAIL: {
    // User flows
    VERIFY: `${API_BASE_URL}/api/email/verify-email`,
    REACTIVATE_ACCOUNT: `${API_BASE_URL}/api/email/reactivate-account`,

    // Admin flows (location-scoped)
    SEND_VERIFICATION_EMAIL: (id) => `${API_BASE_URL}/api/email/admin/send-verification-email/${id}`,
    SEND_REACTIVATION_EMAIL: (id) => `${API_BASE_URL}/api/email/admin/send-reactivation-email/${id}`,
  },

  // Export endpoints (POST only — filtered full dataset; GET returns 410)
  EXPORT: {
    COMPLAINTS_PDF: `${API_BASE_URL}/api/export/complaints/pdf`,
    COMPLAINTS_EXCEL: `${API_BASE_URL}/api/export/complaints/excel`,
    DEVICES_PDF: `${API_BASE_URL}/api/export/devices/pdf`,
    DEVICES_EXCEL: `${API_BASE_URL}/api/export/devices/excel`,
    MAINTENANCE_PDF: `${API_BASE_URL}/api/export/maintenance/pdf`,
    MAINTENANCE_EXCEL: `${API_BASE_URL}/api/export/maintenance/excel`,
    MY_WORK_LOGS_PDF: `${API_BASE_URL}/api/export/my-work-logs/pdf`,
    MY_WORK_LOGS_EXCEL: `${API_BASE_URL}/api/export/my-work-logs/excel`,
    ACTIVITY_LOG_EXCEL: `${API_BASE_URL}/api/export/activity-log/excel`,
  },

  ACTIVITY_LOG: {
    LIST: `${API_BASE_URL}/api/activity-log`,
    EXPORT_EXCEL: `${API_BASE_URL}/api/export/activity-log/excel`,
  },

  // Data Import/Export endpoints (Master Data)
  DATA_IMPORT: {
    DOWNLOAD_TEMPLATE: `${API_BASE_URL}/api/data-import/template`,
    IMPORT_DATA: `${API_BASE_URL}/api/data-import/import`,
    EXPORT_DATA: `${API_BASE_URL}/api/data-import/export`,
  },

  // Work Logs endpoints
  WORK_LOGS: {
    GET_SUMMARY: `${API_BASE_URL}/api/work-logs/summary`,
  },

  // Device Names endpoints
  DEVICE_NAMES: {
    LIST: `${API_BASE_URL}/api/device-names`,
    LIST_ALL: `${API_BASE_URL}/api/device-names?active_only=false`,
    CREATE: `${API_BASE_URL}/api/device-names`,
    UPDATE: (id) => `${API_BASE_URL}/api/device-names/${id}`,
    DELETE: (id) => `${API_BASE_URL}/api/device-names/${id}`,
    ENABLE: (id) => `${API_BASE_URL}/api/device-names/${id}/enable`,
    DISABLE: (id) => `${API_BASE_URL}/api/device-names/${id}/disable`,
  },

  // Complaint Categories endpoints
  COMPLAINT_CATEGORIES: {
    LIST: `${API_BASE_URL}/api/complaint-categories`,
    CREATE: `${API_BASE_URL}/api/complaint-categories`,
    GET: (id) => `${API_BASE_URL}/api/complaint-categories/${id}`,
    UPDATE: (id) => `${API_BASE_URL}/api/complaint-categories/${id}`,
    DELETE: (id) => `${API_BASE_URL}/api/complaint-categories/${id}`,
    APPLICABLE: (params) => `${API_BASE_URL}/api/complaint-categories/applicable?${params}`,
  },

  // Dashboard endpoints
  DASHBOARD: {
    BASE: `${API_BASE_URL}/api/dashboard`,
    GET: `${API_BASE_URL}/api/dashboard`,
    USER_ROLES_BY_DEPT: (deptCode) => `${API_BASE_URL}/api/dashboard/user-roles-by-dept?dept=${deptCode}`,
  },

  // Alerts endpoints
  ALERTS: {
    LIST: `${API_BASE_URL}/api/alerts`,
    FILTERS: `${API_BASE_URL}/api/alerts/filters`,
    FILTER: `${API_BASE_URL}/api/alerts/filter`,
    REFRESH: `${API_BASE_URL}/api/alerts/refresh`,
    ACKNOWLEDGE: (alertId) => `${API_BASE_URL}/api/alerts/${alertId}/acknowledge`,
    SNOOZE: (alertId) => `${API_BASE_URL}/api/alerts/${alertId}/snooze`,
    UNSNOOZE: (alertId) => `${API_BASE_URL}/api/alerts/${alertId}/unsnooze`,
    ARCHIVE: (alertId) => `${API_BASE_URL}/api/alerts/${alertId}/archive`,
  },

  // Documents endpoints
  DOCUMENTS: {
    GET: (deviceId, docId) => `${API_BASE_URL}/api/devices/${deviceId}/documents/${docId}`,
    CLEANUP: `${API_BASE_URL}/api/admin/documents/cleanup`,
  },

  // Stats endpoints
  STATS: {
    ALL: `${API_BASE_URL}/api/stats`,
    SUPERADMIN: `${API_BASE_URL}/api/stats/superadmin`,
  },

  // Audit endpoints
  AUDIT: {
    CATEGORIES: (entityType) => `${API_BASE_URL}/api/audit/categories/${entityType}`,
  },

  // Audit Viewer endpoints (cross-entity)
  AUDIT_VIEWER: {
    ENTITIES: `${API_BASE_URL}/api/audit/entities`,
    TRANSACTIONS: `${API_BASE_URL}/api/audit/transactions`,
    TRANSACTIONS_EXPORT_EXCEL: `${API_BASE_URL}/api/audit/transactions/export/excel`,
    TRANSACTION: (id) => `${API_BASE_URL}/api/audit/transactions/${id}`,
    CATEGORIES: (entityType) => `${API_BASE_URL}/api/audit/categories/${entityType}`,
  },

  // Schedule endpoints
  SCHEDULE: {
    LIST: `${API_BASE_URL}/api/schedule`,
  },

  // Analytics endpoints
  ANALYTICS: {
    OVERVIEW: `${API_BASE_URL}/api/analytics/overview`,
    MAINTENANCE_REPORT: `${API_BASE_URL}/api/analytics/maintenance-report`,
  },

  // Trends Analytics endpoints
  TRENDS: {
    COMPLIANCE: `${API_BASE_URL}/api/trends/compliance`,
    LOCATIONS: `${API_BASE_URL}/api/trends/locations`,
    DEPARTMENTS: (locationId) => `${API_BASE_URL}/api/trends/departments/${locationId}`,
    DEPARTMENTS_ATTENTION: `${API_BASE_URL}/api/trends/departments/attention`,
    FORECAST: `${API_BASE_URL}/api/trends/forecast`,
  },

  // Maintenance endpoints
  MAINTENANCE: {
    MY_LOGS: `${API_BASE_URL}/api/my-maintenance-logs`,
    HISTORY: `${API_BASE_URL}/api/devices/maintenance-history`,
    DETAIL: (id) => `${API_BASE_URL}/api/maintenance/${id}`,
  },
};

export { API_BASE_URL };
